// StaleVersionDialog — shown when the save RPC returns a stale_version error.
// The user can view the other author's changes, reload (discarding their edits), or cancel.

import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

interface StaleVersionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Display name of the author who saved while this user was editing. */
  otherAuthor: string;
  /** The version number they saved (the current DB version). */
  theirVersion: number;
  /** The version this user loaded. */
  myVersion: number;
  /** The current DB content (their version) for side-by-side comparison. */
  theirContent?: { frontmatter: string; body: string };
  /** The unsaved local content for comparison. */
  myContent?: { frontmatter: string; body: string };
  /** Reload the file from DB, discarding local edits. */
  onReload: () => void;
  /** Open the diff view. */
  onViewChanges: () => void;
}

export function StaleVersionDialog({
  open,
  onOpenChange,
  otherAuthor,
  theirVersion,
  myVersion,
  theirContent,
  myContent,
  onReload,
  onViewChanges,
}: StaleVersionDialogProps) {
  const [diffOpen, setDiffOpen] = useState(false);

  function handleViewChanges() {
    onViewChanges();
    setDiffOpen(true);
  }

  function handleReload() {
    onReload();
    onOpenChange(false);
  }

  function handleCancel() {
    onOpenChange(false);
  }

  return (
    <>
      <AlertDialog open={open} onOpenChange={onOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>This file was edited</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <span className="font-semibold text-foreground">{otherAuthor}</span>
                {" saved version "}
                <span className="font-semibold text-foreground">{theirVersion}</span>
                {" while you were working on version "}
                <span className="font-semibold text-foreground">{myVersion}</span>
                {". Your changes haven't been saved."}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel onClick={handleCancel}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-transparent border border-border text-foreground hover:bg-white/[0.06]"
              onClick={handleViewChanges}
            >
              View their changes
            </AlertDialogAction>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleReload}
            >
              Reload — discard my edits
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Inline diff dialog — shows side-by-side content for manual reconciliation. */}
      <Dialog open={diffOpen} onOpenChange={setDiffOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>
              Version {myVersion} (yours) vs Version {theirVersion} ({otherAuthor})
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 text-xs font-mono">
            <div>
              <p className="text-muted-foreground mb-2 font-sans font-medium text-sm">
                Your edits (unsaved)
              </p>
              <pre className="bg-muted/40 rounded-md p-3 whitespace-pre-wrap break-words overflow-auto max-h-[50vh] border border-border text-xs">
                {myContent ? `${myContent.frontmatter}\n\n${myContent.body}` : "(no local content)"}
              </pre>
            </div>
            <div>
              <p className="text-muted-foreground mb-2 font-sans font-medium text-sm">
                Current DB version ({otherAuthor})
              </p>
              <pre className="bg-muted/40 rounded-md p-3 whitespace-pre-wrap break-words overflow-auto max-h-[50vh] border border-border text-xs">
                {theirContent
                  ? `${theirContent.frontmatter}\n\n${theirContent.body}`
                  : "(loading…)"}
              </pre>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
